import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { formStyles } from './shared-styles.js';
import { AuthService, authService as defaultAuthService } from '../services/auth-service.js';
import { isApiError } from '../services/api-client.js';

/**
 * Completes a password reset. Rendered on the `/reset-password` page (the target of the link in the
 * reset email), it receives the account `email` and the reset `token` as attributes and collects a
 * new password, which it posts to `/api/auth/reset-password`. On success it shows a confirmation with
 * a link to sign in.
 */
@customElement('gc-reset-password-form')
export class GcResetPasswordForm extends LitElement {
  static override styles = formStyles;

  /** Injectable for testing; defaults to the shared AuthService. */
  authService: AuthService = defaultAuthService;

  /** The account email (from the reset link). */
  @property({ type: String }) email = '';

  /** The single-use reset token (from the reset link). */
  @property({ type: String }) token = '';

  @state() private loading = false;
  @state() private error = '';
  @state() private done = false;

  override render() {
    if (this.done) {
      return html`<div class="success" role="status">
        <p>Your password has been reset.</p>
        <p><a href="/login">Sign in</a> with your new password.</p>
      </div>`;
    }
    if (!this.token || !this.email) {
      return html`<p class="error" role="alert">This password reset link is invalid or has expired.</p>`;
    }
    return html`
      <form @submit=${this.onSubmit}>
        <label>
          New password
          <input type="password" name="newPassword" autocomplete="new-password" required minlength="8" />
        </label>
        <div class="error" role="alert">${this.error}</div>
        <button type="submit" ?disabled=${this.loading}>
          ${this.loading ? 'Saving…' : 'Reset password'}
        </button>
      </form>
    `;
  }

  private async onSubmit(event: Event) {
    event.preventDefault();
    if (this.loading) {
      return;
    }
    const newPassword = String(new FormData(event.target as HTMLFormElement).get('newPassword') ?? '');

    this.error = '';
    this.loading = true;
    try {
      await this.authService.resetPassword(this.email, this.token, newPassword);
      this.done = true;
    } catch (err) {
      this.error = isApiError(err) ? err.message : 'Could not reset your password. The link may have expired.';
    } finally {
      this.loading = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-reset-password-form': GcResetPasswordForm;
  }
}
